// src/pages/seasons/EventDetailPage.jsx
// Unified event detail + attendance roll-call.
// Route: /seasons/:seasonId/schedule/:eventId
//
// Upcoming events: event info + per-player SMS RSVP breakdown + nudge
// Past events:     event info + inline result entry (games) + attendance roll-call
//                  SMS RSVP (yes/no) shown as context next to each player's attendance mark
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, getDocs, addDoc, updateDoc, writeBatch,
  collection, query, where, onSnapshot, serverTimestamp,
  orderBy, limit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { useRole } from '@/context/RoleContext';
import { format, isPast, isToday, differenceInDays } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import {
  ArrowLeftIcon,
  PencilSquareIcon,
  ClockIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  TrophyIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathRoundedSquareIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  ShareIcon,
  CloudIcon,
  ArrowDownIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { app } from '@/firebase/firebase';
import { CheckCircleIcon as CheckSolid } from '@heroicons/react/24/solid';
import useNudgeCooldown from '@/hooks/useNudgeCooldown';
import NotifyComposerSlideOver from '@/components/messages/NotifyComposerSlideOver';
import { SparklesIcon } from '@heroicons/react/24/outline';
import TeamSubNav from '@/components/team/TeamSubNav';

/* ── Event type config ───────────────────────────────────────────────── */
const EVENT_TYPES = [
  { value: 'game',       label: 'Game',       emoji: '⚽', headerBg: 'bg-blue-600',    subText: 'text-blue-200'    },
  { value: 'practice',   label: 'Practice',   emoji: '🏃', headerBg: 'bg-emerald-600', subText: 'text-emerald-200' },
  { value: 'tournament', label: 'Tournament', emoji: '🏆', headerBg: 'bg-purple-700',  subText: 'text-purple-200'  },
  { value: 'meeting',    label: 'Meeting',    emoji: '📋', headerBg: 'bg-amber-600',   subText: 'text-amber-200'   },
  { value: 'other',      label: 'Other',      emoji: '📅', headerBg: 'bg-zinc-600',    subText: 'text-zinc-300'    },
];

function getTypeInfo(eventType) {
  return EVENT_TYPES.find(t => t.value === (eventType || 'game')) || EVENT_TYPES[0];
}

function eventDisplayTitle(event) {
  if ((event?.eventType || 'game') === 'game') return `vs ${event?.opponent || 'TBD'}`;
  return event?.title || getTypeInfo(event?.eventType).label;
}

/* ── Helpers ─────────────────────────────────────────────────────────── */
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

function resultLabel(event) {
  if ((event?.eventType || 'game') !== 'game') return null;
  const { homeScore, awayScore, homeAway } = event || {};
  if (homeScore == null || awayScore == null) return null;
  const teamScore = homeAway === 'home' ? homeScore : awayScore;
  const oppScore  = homeAway === 'home' ? awayScore : homeScore;
  if (teamScore > oppScore) return { text: `W ${teamScore}–${oppScore}`, outcome: 'win' };
  if (teamScore < oppScore) return { text: `L ${teamScore}–${oppScore}`, outcome: 'loss' };
  return { text: `D ${teamScore}–${oppScore}`, outcome: 'draw' };
}

const STATUS = { present: 'present', absent: 'absent', unset: 'unset' };
const HEAD_FONT = "'Barlow Condensed', sans-serif";

const SESSION_TYPE_MAP = {
  game: 'Game', practice: 'Practice', tournament: 'Tournament',
  meeting: 'Other', other: 'Other',
};

/* ── Location helpers (map embed + native maps deep-link) ───────────── */

/** Best string we have to feed to a maps URL. Prefers full address. */
function locationQuery(event) {
  if (!event) return null;
  const addr = (event.fieldAddress || '').trim();
  const name = (event.fieldName || '').trim();
  if (addr) return name ? `${name}, ${addr}` : addr;
  if (name) return name;
  return null;
}

/** No-API-key Google Maps iframe embed. Works for both addresses and
 *  named locations ("Griffith Park Field 4") via `q=`. */
function mapsEmbedUrl(query) {
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;
}

/** Detect platform so we can hand the user off to the maps app they
 *  expect — Apple Maps on iOS, Google Maps on Android, whatever's
 *  installed elsewhere. */
function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'web';
}

/** Deep-link to the right native maps app:
 *    iOS     → http://maps.apple.com/?daddr=… (opens Apple Maps app
 *              if installed, else Apple's web maps in Safari)
 *    Android → https://www.google.com/maps/dir/?api=1&destination=…
 *              (opens Google Maps app if installed, else web)
 *    Web     → Google Maps web universal URL
 *
 *  All three give "directions from current location" — the user
 *  doesn't need to type anything.
 */
function directionsUrl(query) {
  const q = encodeURIComponent(query);
  const platform = detectPlatform();
  if (platform === 'ios') {
    return `https://maps.apple.com/?daddr=${q}`;
  }
  // Android + web both use Google's universal maps URL — Android
  // opens Google Maps app via App Links automatically.
  return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}

/* ── iCalendar (.ics) generator for "Add to calendar" ──────────────── */

function pad2(n) { return String(n).padStart(2, '0'); }

/** Format as iCal UTC: YYYYMMDDTHHMMSSZ */
function toIcsUtc(d) {
  return (
    d.getUTCFullYear() +
    pad2(d.getUTCMonth() + 1) +
    pad2(d.getUTCDate()) +
    'T' +
    pad2(d.getUTCHours()) +
    pad2(d.getUTCMinutes()) +
    pad2(d.getUTCSeconds()) +
    'Z'
  );
}

function buildIcs({ event, eventId, kickoff, endDate, endsAt, displayTitle }) {
  const start = kickoff || new Date();
  // Prefer endsAt (real end timestamp shipped May 2026 on event creation),
  // fall back to legacy endDate (multi-day tournament end day, time-of-day
  // unspecified), fall back to a 90-min default for legacy events without
  // either field set.
  const end = endsAt
    || endDate
    || new Date(start.getTime() + 90 * 60 * 1000);

  const loc = [event.fieldName, event.fieldAddress].filter(Boolean).join(', ');

  // Escape iCal text per RFC 5545 (basic — ; , and \ get escaped, newlines → \n).
  const esc = (s) => String(s || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/[,;]/g, (c) => '\\' + c);

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Scrimly//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${eventId}@app.getscrimly.com`,
    `DTSTAMP:${toIcsUtc(new Date())}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${esc(displayTitle)}`,
    loc ? `LOCATION:${esc(loc)}` : null,
    event.notes ? `DESCRIPTION:${esc(event.notes)}` : null,
    `URL:${esc(`https://app.getscrimly.com/seasons/${event.seasonId || ''}/schedule/${eventId}`)}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].filter(Boolean);

  return lines.join('\r\n');
}

/* ── Map-app preference (iOS only — Android opens Google Maps app
       automatically via App Links) ──────────────────────────────────── */
const MAP_PREF_KEY = 'scrimly.mapPref';

function getMapPreference() {
  try { return localStorage.getItem(MAP_PREF_KEY) || null; } catch { return null; }
}
function setMapPreference(value) {
  try { localStorage.setItem(MAP_PREF_KEY, value); } catch { /* noop */ }
}

function appleMapsUrl(query) {
  return `https://maps.apple.com/?daddr=${encodeURIComponent(query)}`;
}
function googleMapsUrl(query) {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;
}

/* ── Weather chip via Open-Meteo (free, no API key) ─────────────────
   Geocodes the address with their geocoding endpoint, then pulls the
   forecast for the closest hour to kickoff. Forecast horizon is ~7 days
   so we silently skip events further out. */
const WEATHER_CACHE_KEY = (q) => `scrimly.wx:${q.slice(0, 80)}`;

function wmoEmoji(code) {
  if (code == null) return '🌤';
  if (code === 0) return '☀️';
  if (code <= 3) return '🌤';
  if (code <= 48) return '🌫️';
  if (code <= 67) return '🌧️';
  if (code <= 77) return '🌨️';
  if (code <= 82) return '🌦️';
  if (code <= 86) return '❄️';
  if (code >= 95) return '⛈️';
  return '🌤';
}

function wmoLabel(code) {
  if (code == null) return 'Forecast';
  if (code === 0) return 'Clear';
  if (code <= 3) return 'Partly cloudy';
  if (code <= 48) return 'Fog';
  if (code <= 57) return 'Drizzle';
  if (code <= 67) return 'Rain';
  if (code <= 77) return 'Snow';
  if (code <= 82) return 'Showers';
  if (code <= 86) return 'Snow showers';
  if (code >= 95) return 'Thunderstorm';
  return 'Forecast';
}

async function fetchForecast({ address, when }) {
  if (!address || !when) return null;
  // Only forecast within ~7 days — Open-Meteo's free forecast horizon.
  const days = differenceInDays(when, new Date());
  if (days < -1 || days > 14) return null;

  const cacheKey = WEATHER_CACHE_KEY(address);
  // 30-min cache to avoid hammering the API on re-renders
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    if (cached && Date.now() - cached.ts < 30 * 60 * 1000) {
      return cached.value;
    }
  } catch { /* noop */ }

  // 1. Geocode
  const geo = await fetch(
    `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(address)}&count=1`
  ).then((r) => r.ok ? r.json() : null).catch(() => null);
  const place = geo?.results?.[0];
  if (!place) return null;
  const { latitude, longitude } = place;

  // 2. Forecast — pull hourly for the date of the event
  const dateStr = when.toISOString().slice(0, 10); // YYYY-MM-DD
  const fc = await fetch(
    `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}` +
    `&hourly=temperature_2m,precipitation_probability,weather_code,wind_speed_10m` +
    `&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto` +
    `&start_date=${dateStr}&end_date=${dateStr}`
  ).then((r) => r.ok ? r.json() : null).catch(() => null);

  if (!fc?.hourly?.time?.length) return null;

  // Pick the hour closest to kickoff
  const targetMs = when.getTime();
  let bestIdx = 0;
  let bestDelta = Infinity;
  fc.hourly.time.forEach((iso, i) => {
    const delta = Math.abs(new Date(iso).getTime() - targetMs);
    if (delta < bestDelta) { bestDelta = delta; bestIdx = i; }
  });

  const value = {
    code:    fc.hourly.weather_code?.[bestIdx],
    tempF:   fc.hourly.temperature_2m?.[bestIdx],
    precip:  fc.hourly.precipitation_probability?.[bestIdx],
    windMph: fc.hourly.wind_speed_10m?.[bestIdx],
    fetchedAt: Date.now(),
  };
  try { sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), value })); } catch { /* noop */ }
  return value;
}

function downloadIcs(filename, content) {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so iOS Safari has time to load the blob
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/* ── Get or create linked attendance session ─────────────────────────── */
async function getOrCreateSession(event, seasonId, teamId) {
  // 1. Check if game doc already links to a session
  if (event.attendanceSessionId) {
    const snap = await getDoc(doc(db, 'sessions', event.attendanceSessionId));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
  }

  // 2. Query sessions by scheduleEventId.
  // Must include teamId — the sessions rule gates on
  // isTeamStaff(resource.data.teamId), and Firestore can't statically
  // prove a scheduleEventId-only query is safe under the Phase 2
  // lockdown (catch-all = if false). We have the teamId passed in
  // as an arg, so include it in the query.
  const q    = query(
    collection(db, 'sessions'),
    where('scheduleEventId', '==', event.id),
    where('teamId',          '==', teamId),
  );
  const snap = await getDocs(q);
  if (!snap.empty) {
    const session = { id: snap.docs[0].id, ...snap.docs[0].data() };
    // Backfill the link on the games doc
    if (!event.attendanceSessionId) {
      await updateDoc(doc(db, 'games', event.id), { attendanceSessionId: session.id });
    }
    return session;
  }

  // 3. Create a new linked session
  const isGame     = (event.eventType || 'game') === 'game';
  const sessionRef = await addDoc(collection(db, 'sessions'), {
    scheduleEventId: event.id,
    seasonId,
    teamId,
    type:      SESSION_TYPE_MAP[event.eventType] || 'Game',
    title:     isGame ? `vs ${event.opponent || 'TBD'}` : (event.title || 'Event'),
    date:      event.date,
    time:      null,
    location:  event.fieldName
      ? { name: event.fieldName, address: event.fieldAddress || null }
      : null,
    note:      null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateDoc(doc(db, 'games', event.id), { attendanceSessionId: sessionRef.id });
  return { id: sessionRef.id };
}

/* ── Weather chip ───────────────────────────────────────────────────── */

function WeatherChip({ address, when }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const v = await fetchForecast({ address, when });
      if (!cancelled) { setData(v); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [address, when?.getTime()]);

  if (loading) return null;
  if (!data || data.code == null) return null;

  const heavyRain = (data.precip ?? 0) >= 60;
  const windy     = (data.windMph ?? 0) >= 20;
  // Audit fix #2: default tone now uses lime/pitch tokens to match the rest
  // of the page; amber stays reserved for the heavy-rain / windy warning state.
  const tone = heavyRain || windy
    ? 'border-amber-200 bg-amber-50 text-amber-900'
    : 'border-[#b5f03d]/40 bg-[#b5f03d]/5 text-[#0d1f14]';

  return (
    <div className={clsx(
      'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold',
      tone,
    )}>
      <span className="text-base leading-none">{wmoEmoji(data.code)}</span>
      <span>{wmoLabel(data.code)}</span>
      {data.tempF != null && <span className="text-zinc-500">· {Math.round(data.tempF)}°F</span>}
      {data.precip != null && data.precip > 10 && (
        <span className="text-zinc-500">· {data.precip}% rain</span>
      )}
      {data.windMph != null && data.windMph > 10 && (
        <span className="text-zinc-500">· {Math.round(data.windMph)}mph wind</span>
      )}
    </div>
  );
}

/* ── Map app picker (iOS only — Android opens Google Maps app
       automatically via App Links, no chooser needed) ───────────────── */

function MapAppPicker({ open, onClose, locationQ }) {
  if (!open) return null;
  const choose = (which) => {
    setMapPreference(which);
    const url = which === 'apple' ? appleMapsUrl(locationQ) : googleMapsUrl(locationQ);
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl border border-zinc-200 shadow-2xl p-4"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#5a8a1f] mb-3 text-center"
           style={{ fontFamily: HEAD_FONT }}>
          Open directions in
        </p>
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => choose('apple')}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] px-4 py-3 text-sm font-bold uppercase tracking-wide text-[#0d1f14] active:scale-[0.97] transition-all"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            🍎 Apple Maps
          </button>
          <button
            type="button"
            onClick={() => choose('google')}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 px-4 py-3 text-sm font-bold uppercase tracking-wide text-[#0d1f14] active:scale-[0.97] transition-all"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            📍 Google Maps
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full text-center py-2 text-xs text-zinc-400 hover:text-zinc-600"
          >
            Cancel
          </button>
        </div>
        <p className="mt-3 text-[11px] text-zinc-400 text-center">
          We'll remember your choice. Change it any time in Settings.
        </p>
      </div>
    </div>
  );
}

/* ── Pull-to-refresh hook ───────────────────────────────────────────── */

function usePullToRefresh({ onRefresh, threshold = 70 }) {
  const [pulling, setPulling]       = useState(false);
  const [pullDist, setPullDist]     = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const isPullingRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onTouchStart = (e) => {
      if (window.scrollY > 0) return; // only at top of page
      if (refreshing) return;
      startY.current = e.touches?.[0]?.clientY ?? 0;
      isPullingRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!isPullingRef.current) return;
      const y = e.touches?.[0]?.clientY ?? 0;
      const dy = y - startY.current;
      if (dy > 0) {
        // Damp the pull so it doesn't feel like a free-fall stretch.
        const damped = Math.min(dy * 0.5, 120);
        setPulling(true);
        setPullDist(damped);
      }
    };

    const onTouchEnd = async () => {
      if (!isPullingRef.current) return;
      isPullingRef.current = false;
      const triggered = pullDist >= threshold;
      setPulling(false);
      setPullDist(0);
      if (triggered && !refreshing) {
        setRefreshing(true);
        try { await onRefresh?.(); } catch { /* swallow */ }
        // Slight delay so the spinner is visible even on instant refreshes
        setTimeout(() => setRefreshing(false), 600);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove',  onTouchMove,  { passive: true });
    window.addEventListener('touchend',   onTouchEnd);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove',  onTouchMove);
      window.removeEventListener('touchend',   onTouchEnd);
    };
  }, [onRefresh, threshold, refreshing, pullDist]);

  return { pulling, pullDist, refreshing };
}

function PullIndicator({ pulling, pullDist, refreshing, threshold = 70 }) {
  if (!pulling && !refreshing) return null;
  const ready = pullDist >= threshold || refreshing;
  return (
    <div
      className="fixed top-0 inset-x-0 z-40 flex items-center justify-center pointer-events-none"
      style={{
        transform: `translateY(${refreshing ? 32 : Math.min(pullDist, 80)}px)`,
        transition: refreshing ? 'transform 0.15s ease' : 'none',
      }}
    >
      <div className={clsx(
        'flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-zinc-200 transition-transform',
        ready ? 'rotate-180' : '',
      )}>
        <ArrowPathIcon className={clsx('h-5 w-5 text-[#5a8a1f]', refreshing && 'animate-spin')} />
      </div>
    </div>
  );
}

/* ── Parent RSVP block ──────────────────────────────────────────────── */

function ParentRsvpBlock({ event, eventId }) {
  const { firebaseUser } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [rsvps, setRsvps]                 = useState({});
  const [loading, setLoading]             = useState(true);
  const [submittingId, setSubmittingId]   = useState(null);

  useEffect(() => {
    if (!firebaseUser?.email || !event?.seasonId) return;
    let cancelled = false;
    (async () => {
      try {
        const email = String(firebaseUser.email).trim().toLowerCase();
        const snap = await getDocs(query(
          collection(db, 'registrations'),
          where('parentEmail', '==', email),
          where('seasonId',    '==', event.seasonId),
          limit(10),
        ));
        if (cancelled) return;
        const regs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRegistrations(regs);
        // Pull existing RSVPs for each registration in parallel
        const rsvpEntries = await Promise.all(regs.map(async (reg) => {
          try {
            const rsnap = await getDoc(doc(db, 'games', eventId, 'rsvps', reg.id));
            return [reg.id, rsnap.exists() ? rsnap.data().response : null];
          } catch { return [reg.id, null]; }
        }));
        if (cancelled) return;
        setRsvps(Object.fromEntries(rsvpEntries));
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[ParentRsvp] lookup failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [firebaseUser?.email, event?.seasonId, eventId]);

  const handleRsvp = async (reg, response) => {
    setSubmittingId(reg.id);
    const tid = toast.loading('Saving RSVP…');
    try {
      const fn = httpsCallable(getFunctions(app, 'us-central1'), 'submitGameRsvp');
      await fn({ gameId: eventId, registrationId: reg.id, response });
      setRsvps((prev) => ({ ...prev, [reg.id]: response }));
      toast.success(response === 'yes' ? 'See you there!' : 'Thanks for letting us know', { id: tid });
    } catch (err) {
      toast.error(err?.message || 'Could not save RSVP', { id: tid });
    } finally {
      setSubmittingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5">
        <div className="h-12 rounded-xl bg-zinc-100 animate-pulse" />
      </div>
    );
  }
  if (!registrations.length) return null;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
      <div className="border-b border-zinc-100 px-5 py-3 flex items-center gap-2">
        <CheckCircleIcon className="h-4 w-4 text-[#5a8a1f]" />
        <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}>
          Will you be there?
        </h2>
      </div>
      <div className="p-5 space-y-3">
        {registrations.map((reg) => {
          const current = rsvps[reg.id];
          const playerName = reg.playerName
            || [reg.playerFirstName, reg.playerLastName].filter(Boolean).join(' ')
            || 'Your player';
          const submitting = submittingId === reg.id;
          return (
            <div key={reg.id} className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
              <p className="text-sm font-semibold text-[#0d1f14] flex-1 min-w-0 truncate">
                {playerName}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleRsvp(reg, 'yes')}
                  disabled={submitting}
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all active:scale-[0.97] disabled:opacity-50',
                    current === 'yes'
                      ? 'bg-emerald-600 text-white shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]'
                      : 'border border-zinc-200 bg-white text-zinc-700 hover:border-emerald-400 hover:text-emerald-700',
                  )}
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => handleRsvp(reg, 'no')}
                  disabled={submitting}
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all active:scale-[0.97] disabled:opacity-50',
                    current === 'no'
                      ? 'bg-red-600 text-white shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]'
                      : 'border border-zinc-200 bg-white text-zinc-700 hover:border-red-400 hover:text-red-700',
                  )}
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <XCircleIcon className="h-4 w-4" />
                  No
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── EventDetailPage ─────────────────────────────────────────────────── */
export default function EventDetailPage() {
  const { seasonId, eventId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { teamRole } = useRole();

  const [event,         setEvent]         = useState(null);
  const [season,        setSeason]        = useState(null);
  const [session,       setSession]       = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [smsRsvps,      setSmsRsvps]      = useState({}); // { registrationId: 'yes'|'no' }
  const [records,       setRecords]       = useState({}); // { registrationId: status }

  const [loading,        setLoading]        = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [dirty,          setDirty]          = useState(false);
  const [saving,         setSaving]         = useState(false);
  // Audit fix #6: surface the last save failure inline so the coach has a
  // visible "Retry save" button — the toast disappears in seconds and the
  // dirty state is the only other signal that something went wrong.
  const [inlineSaveError, setInlineSaveError] = useState(null);

  // Per-team role for THIS event's team. Parents see a stripped-down
  // view (their kid's game/practice details) without the coach
  // affordances: Edit Event, Text non-responders, per-player RSVP
  // breakdown, Edit Score, Take Attendance. canManage gates each
  // coach-only block. Falls back to "no role yet" while the event
  // is still loading — no UI is rendered during that state anyway.
  const isParent  = teamRole(event?.teamId) === 'parent';
  const canManage = !isParent;

  // Inline result entry
  const [editingResult, setEditingResult] = useState(false);
  const [teamScore,     setTeamScore]     = useState('');
  const [oppScore,      setOppScore]      = useState('');
  const [shareResult,   setShareResult]   = useState(false);
  const [savingResult,  setSavingResult]  = useState(false);

  // Smart composer slide-over — opened by per-context buttons elsewhere
  // on the page (Share Recap on past games with results, Send Reminder
  // on upcoming events). Single instance handles both via messageType.
  const [composerType, setComposerType] = useState(null); // 'Game Recap' | 'Reminder' | null

  // Attendance filter
  const [attFilter, setAttFilter] = useState('all');

  // Map app picker (iOS only — shown the first time a user taps Directions
  // unless they've already saved a preference)
  const [mapPickerOpen, setMapPickerOpen] = useState(false);

  // Cancel-event modal — wired in the Coach Actions rail just below the
  // event info card. Calls the same sendGameUpdateSms Cloud Function the
  // SeasonSchedule page uses, so cancellations from either surface fire
  // identical email + SMS notifications.
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling,    setCancelling]    = useState(false);

  // Pull-to-refresh — re-fetches the event doc + RSVPs
  const [refreshTick, setRefreshTick] = useState(0);
  const handleRefresh = useCallback(async () => {
    setRefreshTick((n) => n + 1);
    // Wait briefly so the spinner is visible even on instant cache hits
    await new Promise((r) => setTimeout(r, 350));
  }, []);
  const ptr = usePullToRefresh({ onRefresh: handleRefresh });

  /* ── Derived ── */
  const kickoff     = toDate(event?.date);
  const endDate     = toDate(event?.endDate);   // legacy: multi-day tournament END day
  // endsAt was added May 2026 on event creation. For single-day events
  // it's same-day as kickoff with the end clock-time. For multi-day
  // tournaments it's the end clock-time on event.endDate. Older events
  // (pre-May 2026) don't have it — fall back gracefully (no end-time
  // text shown rather than guessing).
  const endsAt      = toDate(event?.endsAt);
  // True same-day end — used to render the compact "5:00 – 6:30 PM"
  // form. Multi-day tournaments fall through to a longer form.
  const sameDayEnd  = !!(endsAt && kickoff && (
    endsAt.getFullYear() === kickoff.getFullYear() &&
    endsAt.getMonth()    === kickoff.getMonth() &&
    endsAt.getDate()     === kickoff.getDate()
  ));
  const isPastEvent = kickoff ? isPast(kickoff) : false;
  const isGame      = (event?.eventType || 'game') === 'game';
  const result      = resultLabel(event);
  const typeInfo    = getTypeInfo(event?.eventType);

  /* ── Load event + season (re-runs on pull-to-refresh tick) ── */
  useEffect(() => {
    Promise.all([
      getDoc(doc(db, 'games',   eventId)),
      getDoc(doc(db, 'seasons', seasonId)),
    ]).then(([eSnap, sSnap]) => {
      if (eSnap.exists()) setEvent({ id: eSnap.id, ...eSnap.data() });
      if (sSnap.exists()) setSeason({ id: sSnap.id, ...sSnap.data() });
      setLoading(false);
    });
  }, [eventId, seasonId, refreshTick]);

  /* ── Get or create linked attendance session ── */
  useEffect(() => {
    if (!event || !season) return;
    setSessionLoading(true);
    getOrCreateSession(event, seasonId, season.teamId)
      .then(s => setSession(s))
      .catch(err => console.error('[EventDetail] session sync:', err))
      .finally(() => setSessionLoading(false));
  }, [event?.id, season?.teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Live registrations ──
   *
   * Must filter by BOTH seasonId AND teamId. The Phase 2 lockdown of the
   * registrations rule gates reads on isTeamStaff(resource.data.teamId);
   * Firestore's rules engine can't statically prove a seasonId-only query
   * is safe (the rule wraps teamId in a function call which isn't pattern-
   * matchable). Without the teamId clause this listener fails permission-
   * denied silently.
   */
  useEffect(() => {
    if (!seasonId || !season?.teamId) return;
    const q = query(
      collection(db, 'registrations'),
      where('seasonId', '==', seasonId),
      where('teamId',   '==', season.teamId),
      where('status',   'in', ['confirmed', 'paid']),
    );
    return onSnapshot(q, snap => {
      const regs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      regs.sort((a, b) => (a.playerName || '').localeCompare(b.playerName || ''));
      setRegistrations(regs);
    }, (err) => {
      if (import.meta.env.DEV) console.warn('[EventDetail] registrations subscribe failed', err);
    });
  }, [seasonId, season?.teamId]);

  /* ── SMS RSVPs (one-shot) ── */
  useEffect(() => {
    getDocs(collection(db, 'games', eventId, 'rsvps')).then(snap => {
      const map = {};
      snap.forEach(d => { map[d.id] = d.data().response; });
      setSmsRsvps(map);
    });
  }, [eventId]);

  /* ── Live attendance records ── */
  useEffect(() => {
    if (!session?.id) return;
    const q = query(collection(db, 'attendance'), where('sessionId', '==', session.id));
    return onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => {
        const data = d.data();
        map[data.registrationId] = data.status;
      });
      setRecords(map);
      setDirty(false);
    });
  }, [session?.id]);

  /* ── Seed result edit fields from event ── */
  useEffect(() => {
    if (!event || !isGame) return;
    const isHome = event.homeAway === 'home';
    setTeamScore(event.homeScore != null ? String(isHome ? event.homeScore : event.awayScore) : '');
    setOppScore(event.awayScore  != null ? String(isHome ? event.awayScore  : event.homeScore) : '');
  }, [event?.homeScore, event?.awayScore, event?.homeAway, isGame]); // eslint-disable-line

  /* ── Attendance toggles ── */
  const toggle = useCallback((registrationId) => {
    setRecords(prev => {
      const cur  = prev[registrationId] ?? STATUS.unset;
      const next = cur === STATUS.unset ? STATUS.present : cur === STATUS.present ? STATUS.absent : STATUS.unset;
      return { ...prev, [registrationId]: next };
    });
    setDirty(true);
  }, []);

  const markAll = useCallback((status) => {
    // Audit fix #5: confirm BEFORE clobbering existing marks so a stray tap
    // on "Mark all → Present" doesn't silently overwrite a half-finished
    // roll call. We count records that already have a non-unset value and
    // ask once. If the roster is empty (everyone unset) we keep the
    // original one-tap fast path — no friction when there's nothing to lose.
    const existingMarks = registrations.filter(
      r => records[r.id] && records[r.id] !== STATUS.unset
    ).length;
    if (existingMarks > 0) {
      const labelMap = { present: 'Present', absent: 'Absent', unset: 'Unmarked' };
      const ok = window.confirm(
        `Overwrite ${existingMarks} existing mark${existingMarks === 1 ? '' : 's'} with '${labelMap[status] || status}'?`
      );
      if (!ok) return;
    }
    setRecords(prev => {
      const next = { ...prev };
      registrations.forEach(r => { next[r.id] = status; });
      return next;
    });
    setDirty(true);
  }, [registrations, records]);

  /* ── Save attendance ── */
  const saveAttendance = async () => {
    if (!session?.id) return;
    setSaving(true);
    try {
      const batch = writeBatch(db);
      registrations.forEach(player => {
        const status = records[player.id] ?? STATUS.unset;
        batch.set(
          doc(db, 'attendance', `${session.id}_${player.id}`),
          {
            sessionId:      session.id,
            seasonId,
            registrationId: player.id,
            playerName:     player.playerName,
            status,
            updatedAt:      serverTimestamp(),
          },
          { merge: true }
        );
      });
      const present = registrations.filter(r => records[r.id] === STATUS.present).length;
      batch.update(doc(db, 'sessions', session.id), {
        attendanceCount: registrations.length,
        presentCount:    present,
        updatedAt:       serverTimestamp(),
      });
      await batch.commit();
      setDirty(false);
      // Clear any prior inline error after a successful save (audit fix #6).
      setInlineSaveError(null);
      toast.success('Attendance saved.');
    } catch (err) {
      console.error('[EventDetail] save attendance:', err);
      toast.error('Failed to save attendance.');
      // Audit fix #6: also stash the message on state so the inline banner
      // can render a recoverable "Retry save" path next to the player list.
      setInlineSaveError(err?.message || 'Network error — your marks weren\'t saved.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Cancel event ── */
  // Marks the event cancelled and fires the same templated email + SMS
  // blast SeasonSchedule's CancelConfirm uses (sendGameUpdateSms with
  // changeType: 'cancellation'). For series events, only the single
  // instance is cancelled here — coaches can use SeasonSchedule for
  // "cancel this and all future" series cancellations (the modal
  // surfaces a hint linking out).
  const handleCancelEvent = async () => {
    setCancelling(true);
    try {
      await updateDoc(doc(db, 'games', eventId), {
        status: 'cancelled',
        updatedAt: serverTimestamp(),
      });
      setEvent(prev => prev ? { ...prev, status: 'cancelled' } : prev);
      try {
        const fn = httpsCallable(getFunctions(app, 'us-central1'), 'sendGameUpdateSms');
        await fn({ gameId: eventId, changeType: 'cancellation' });
        toast.success('Event cancelled · parents notified');
      } catch (notifyErr) {
        console.warn('[EventDetail] cancel notify failed', notifyErr);
        toast.success('Event cancelled');
        toast.error('Notification failed — message parents manually');
      }
      setConfirmCancel(false);
    } catch (err) {
      console.error('[EventDetail] cancel failed', err);
      toast.error('Failed to cancel event.');
    } finally {
      setCancelling(false);
    }
  };

  /* ── Save game result ── */
  const saveResult = async () => {
    const ts = parseInt(teamScore, 10);
    const os = parseInt(oppScore,  10);
    if (isNaN(ts) || isNaN(os) || ts < 0 || os < 0) {
      toast.error('Enter valid scores (0 or more).');
      return;
    }
    setSavingResult(true);
    try {
      const isHome = event.homeAway === 'home';
      const updates = {
        homeScore: isHome ? ts : os,
        awayScore: isHome ? os : ts,
        status:    'completed',
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, 'games', eventId), updates);
      setEvent(prev => ({ ...prev, ...updates }));

      if (shareResult) {
        try {
          await httpsCallable(getFunctions(app, 'us-central1'), 'shareGameResult')({ gameId: eventId });
          toast.success('Result saved · parents notified 🏆');
        } catch {
          toast.success('Result saved');
          toast.error('SMS share failed — notify parents manually');
        }
      } else {
        toast.success('Result saved!');
      }
      setEditingResult(false);
    } catch {
      toast.error('Failed to save result.');
    } finally {
      setSavingResult(false);
    }
  };

  /* ── Stats ── */
  const stats = useMemo(() => {
    const present  = registrations.filter(r => records[r.id] === STATUS.present).length;
    const absent   = registrations.filter(r => records[r.id] === STATUS.absent).length;
    const unmarked = registrations.length - present - absent;
    const smsYes   = Object.values(smsRsvps).filter(v => v === 'yes').length;
    const smsNo    = Object.values(smsRsvps).filter(v => v === 'no').length;
    return { present, absent, unmarked, total: registrations.length, smsYes, smsNo };
  }, [registrations, records, smsRsvps]);

  /* ── Nudge non-responders ──
   * Wrapped in useNudgeCooldown so the button:
   *   • shows a confirm() before sending (so coaches don't spam by mistake)
   *   • disables + shows "Sending…" while the callable is in flight
   *   • disables for a 5-minute cooldown after sending (persisted across
   *     page refreshes via localStorage), with a live countdown label
   *
   * The hook owns toast.success/error — we just refresh local SMS RSVPs
   * if the send actually went through (data not null).
   */
  const nudgeCallable = useMemo(
    () => httpsCallable(getFunctions(app, 'us-central1'), 'sendRsvpNudge'),
    []
  );
  const nonResponderCount = stats.total - stats.smsYes - stats.smsNo;
  const nudge = useNudgeCooldown(eventId, nonResponderCount, nudgeCallable);
  const handleNudge = useCallback(async () => {
    const data = await nudge.send();
    if (data) {
      // Refresh SMS RSVPs so any newly-recorded responses appear.
      getDocs(collection(db, 'games', eventId, 'rsvps')).then(snap => {
        const map = {};
        snap.forEach(d => { map[d.id] = d.data().response; });
        setSmsRsvps(map);
      });
    }
  }, [nudge, eventId]);

  /* ── Filtered player list ── */
  const filteredPlayers = useMemo(() => {
    if (attFilter === 'all') return registrations;
    return registrations.filter(r => (records[r.id] ?? STATUS.unset) === attFilter);
  }, [registrations, records, attFilter]);

  /* ── Loading ── */
  // Skeleton matches the real layout shape (back link → dark header card
  // → details rows → location + map). Pre-fix this rendered three
  // identical 80px grey blocks then snapped into the real layout —
  // huge CLS jolt on slow networks (the exact sideline scenario).
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
          {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
          <TeamSubNav teamId={event?.teamId || season?.teamId} />
          {/* Back link placeholder */}
          <div className="h-5 w-24 rounded-md bg-zinc-200/70 animate-pulse" />

          {/* Event info card placeholder — pitch-dark header + details */}
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
            <div className="px-5 pt-5 pb-5 bg-[#0d1f14]">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-white/10 animate-pulse" />
                <div className="flex-1 space-y-2.5">
                  <div className="h-3 w-16 rounded bg-white/10 animate-pulse" />
                  <div className="h-7 w-48 rounded bg-white/10 animate-pulse" />
                </div>
              </div>
            </div>
            <div className="px-5 py-4 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-zinc-100 animate-pulse" />
                  <div className="h-4 flex-1 max-w-md rounded bg-zinc-100 animate-pulse" />
                </div>
              ))}
            </div>
          </div>

          {/* Location + map placeholder */}
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
            <div className="aspect-[16/9] w-full bg-zinc-100 animate-pulse" />
            <div className="px-5 py-4 space-y-3">
              <div className="h-4 w-1/2 rounded bg-zinc-100 animate-pulse" />
              <div className="flex gap-2">
                <div className="h-9 w-32 rounded-lg bg-zinc-100 animate-pulse" />
                <div className="h-9 w-32 rounded-lg bg-zinc-100 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <p className="text-zinc-500">Event not found.</p>
        <button onClick={() => navigate(`/seasons/${seasonId}/schedule`)}
          className="mt-4 text-[#5a8a1f] hover:text-[#0d1f14] text-sm underline">
          Back to Schedule
        </button>
      </div>
    );
  }

  /* ── Render ── */
  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
      {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
      <TeamSubNav teamId={event?.teamId || season?.teamId} />
      {/* Pull-to-refresh indicator (mobile) — fades in as the user pulls
          down at the top of the page; spins while refreshing. */}
      <PullIndicator
        pulling={ptr.pulling}
        pullDist={ptr.pullDist}
        refreshing={ptr.refreshing}
      />

      {/* Map app picker (iOS first-time prompt) */}
      <MapAppPicker
        open={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        locationQ={locationQuery(event)}
      />

      {/* Cancel-event confirm modal — backdrop click dismisses; the
          confirm action fires the same templated parent-notification
          blast SeasonSchedule uses. For series events a hint links
          out to SeasonSchedule for "cancel this AND all future". */}
      {confirmCancel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget && !cancelling) setConfirmCancel(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 shrink-0 mt-0.5" />
              <div>
                <h3
                  className="font-bold uppercase tracking-tight text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Cancel {isGame ? `vs ${event?.opponent || 'TBD'}` : (event?.title || typeInfo.label)}?
                </h3>
                <p className="text-sm text-zinc-500 mt-1">
                  An email will go to all parents, plus an urgent SMS to opted-in parents.
                </p>
                {event?.seriesId && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                    This is part of a recurring series — only this single event will be cancelled. To
                    cancel future events too, use the <strong>Schedule</strong> page.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmCancel(false)}
                disabled={cancelling}
                className="flex-1 inline-flex items-center justify-center min-h-[44px] rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all disabled:opacity-50"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                Keep it
              </button>
              <button
                onClick={handleCancelEvent}
                disabled={cancelling}
                className="flex-1 inline-flex items-center justify-center min-h-[44px] rounded-xl bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {cancelling ? 'Cancelling…' : 'Yes, cancel + notify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back */}
      <button
        onClick={() => navigate(`/seasons/${seasonId}/schedule`)}
        className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Schedule
      </button>

      {/* ── Event info card ──
          Pitch-dark header with a lime emoji medallion + lime type label.
          Replaces the old per-type background colors (blue/emerald/purple)
          with a unified design that matches the parent landing surface. */}
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
        {/* Pitch-dark header */}
        <div className="px-5 pt-5 pb-5 bg-[#0d1f14] relative overflow-hidden">
          {/* Subtle lime grid pattern in the background, mirrors AcceptInvite */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              {/* Type emoji medallion */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-xl">
                {typeInfo.emoji}
              </div>
              <div className="min-w-0">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-1 flex items-center gap-1.5"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  <span>{typeInfo.label}</span>
                  {event.seriesId && (
                    <span className="inline-flex items-center gap-0.5 text-white/60" title="Part of a recurring series">
                      <ArrowPathRoundedSquareIcon className="h-3 w-3" />
                      <span className="normal-case tracking-normal text-[10px]">recurring</span>
                    </span>
                  )}
                </p>
                <h1
                  className="uppercase text-white leading-[1.05] truncate"
                  style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 24 }}
                >
                  {eventDisplayTitle(event)}
                </h1>
              </div>
            </div>
            {canManage && (
              // 44px min tap target (WCAG 2.5.5) — coach is on a sideline
              // in sun on a phone; the previous 28px chip produced misses
              // and looked unimportant in glare. Sized up + bumped icon
              // and copy size to match the rest of the design system.
              <button
                onClick={() => navigate(`/seasons/${seasonId}/schedule`, { state: { openEditId: eventId } })}
                className="flex-shrink-0 inline-flex items-center gap-1.5 min-h-[44px] px-4 py-2.5 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/10 hover:bg-[#b5f03d]/20 text-[#b5f03d] text-sm font-bold uppercase tracking-wide transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                <PencilSquareIcon className="h-4 w-4" /> Edit
              </button>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="px-5 py-4 space-y-3">
          {kickoff && (
            <div className="flex items-start gap-3 text-sm text-zinc-700 flex-wrap">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <ClockIcon className="h-4 w-4 text-[#5a8a1f]" />
              </div>
              {/* Audit fix #3: multi-day events render as TWO stacked rows
                  ("Starts: …" / "Ends: …") instead of a single inline range
                  that wraps awkwardly on phones. Single-day events keep the
                  inline "5:00 PM – 7:00 PM" range. */}
              {endsAt && !sameDayEnd ? (
                <div className="flex flex-col gap-0.5">
                  <span>
                    <span className="text-zinc-500">Starts: </span>
                    <span className="font-semibold text-[#0d1f14]">{format(kickoff, 'EEE MMM d')}</span>
                    <span className="text-zinc-500">{' · '}{format(kickoff, 'h:mm a')}</span>
                  </span>
                  <span>
                    <span className="text-zinc-500">Ends: </span>
                    <span className="font-semibold text-[#0d1f14]">{format(endsAt, 'EEE MMM d')}</span>
                    <span className="text-zinc-500">{' · '}{format(endsAt, 'h:mm a')}</span>
                  </span>
                </div>
              ) : (
                <span>
                  <span className="font-semibold text-[#0d1f14]">{format(kickoff, 'EEEE, MMMM d')}</span>
                  <span className="text-zinc-500">
                    {' · '}{format(kickoff, 'h:mm a')}
                    {sameDayEnd && (
                      <>{' – '}{format(endsAt, 'h:mm a')}</>
                    )}
                  </span>
                </span>
              )}
              {isToday(kickoff) && (
                // Audit fix #7: motion-safe gate so prefers-reduced-motion
                // users see a static badge instead of a pulsing one.
                <span
                  className="inline-flex items-center rounded-full bg-[#b5f03d] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] motion-safe:animate-pulse"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Today
                </span>
              )}
            </div>
          )}
          {/* Weather forecast — only for events within ~7 days
              and only when we have an address to geocode against. */}
          {kickoff && event.fieldAddress && (
            <WeatherChip address={event.fieldAddress} when={kickoff} />
          )}
          {event.fieldName && (
            <div className="flex items-start gap-3 text-sm text-zinc-700">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mt-0.5">
                <MapPinIcon className="h-4 w-4 text-[#5a8a1f]" />
              </div>
              <span>
                <span className="text-[#0d1f14]">{event.fieldName}</span>
                {event.fieldAddress && <span className="text-zinc-400"> · {event.fieldAddress}</span>}
              </span>
            </div>
          )}
          {event.notes && (
            <p className="text-sm text-zinc-500 italic pl-11">{event.notes}</p>
          )}
          {isGame && event.homeAway && (
            // Audit fix #1: render homeAway as a styled pill ("🏠 Home" lime
            // / "🛫 Away" amber) instead of raw lowercase "home"/"away" text.
            <div className="pl-11">
              <span
                className={clsx(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border',
                  event.homeAway === 'home'
                    ? 'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40'
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                )}
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {event.homeAway === 'home' ? '🏠 Home' : '🛫 Away'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Coach Actions rail ──
          High-priority game-day buttons surfaced right below the event
          summary so they're above the fold even on small phones. Build
          Lineup links to the lineup builder pre-scoped to this event's
          attendance session. Cancel & notify opens a confirm modal
          that fires the same templated email + SMS blast SeasonSchedule
          uses (sendGameUpdateSms · changeType: 'cancellation'). */}
      {canManage && event.status !== 'cancelled' && (
        <div className="flex items-center gap-2 flex-wrap">
          {isGame && !isPastEvent && session?.id && (
            <button
              type="button"
              onClick={() => navigate(`/seasons/${seasonId}/lineup/${session.id}`)}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] text-sm font-bold uppercase tracking-wide shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <ClipboardDocumentListIcon className="h-4 w-4" />
              Build lineup
            </button>
          )}
          {!isPastEvent && (
            <button
              type="button"
              onClick={() => setConfirmCancel(true)}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2.5 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-bold uppercase tracking-wide transition-colors"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <XCircleIcon className="h-4 w-4" />
              Cancel &amp; notify
            </button>
          )}
        </div>
      )}

      {/* ── Score-not-entered nudge ──
          For past games where the coach hasn't recorded a result yet.
          The Edit-score path lives down in the Result section, but
          coaches scanning quickly in glare miss it. This callout
          puts a 60px primary action above the fold; tapping it opens
          the inline form AND scrolls to it. */}
      {canManage && isPastEvent && isGame && !result && event.status !== 'cancelled' && !editingResult && (
        <button
          type="button"
          onClick={() => {
            setEditingResult(true);
            requestAnimationFrame(() => {
              document.getElementById('result-section')?.scrollIntoView({
                behavior: 'smooth', block: 'start',
              });
            });
          }}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 px-5 py-4 transition-all min-h-[60px] text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <TrophyIcon className="h-6 w-6 text-amber-600 shrink-0" />
            <div className="min-w-0">
              <p
                className="text-sm font-bold text-amber-900 uppercase tracking-wide"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
              >
                Score not entered yet
              </p>
              <p className="text-xs text-amber-700 mt-0.5">Tap to record the result.</p>
            </div>
          </div>
          <ArrowDownIcon className="h-4 w-4 text-amber-600 shrink-0" />
        </button>
      )}

      {/* ── Location + actions card ──
          Universally useful — coach OR parent both want a map and a
          one-tap "directions" + "add to calendar". Renders only when
          we have something to point at. Lime/pitch design system to
          match the rest of the parent surface. */}
      {locationQuery(event) && (
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
          <div className="aspect-[16/9] w-full bg-zinc-100">
            <iframe
              title="Field location map"
              src={mapsEmbedUrl(locationQuery(event))}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full border-0 block"
              allowFullScreen
            />
          </div>
          <div className="px-5 py-4 space-y-3">
            {/* Address line — repeated below the map so it's copy-able
                and readable without zooming the embed. Lime icon chip
                matches the SectionCard pattern from EditTeam/Profile. */}
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <MapPinIcon className="h-4 w-4 text-[#5a8a1f]" />
              </div>
              <div className="min-w-0 flex-1 text-sm">
                {event.fieldName && (
                  <p className="font-semibold text-[#0d1f14]">{event.fieldName}</p>
                )}
                {event.fieldAddress && (
                  <p className="text-xs text-zinc-500 break-words mt-0.5">{event.fieldAddress}</p>
                )}
              </div>
            </div>

            {/* Action row — Directions is primary lime CTA; calendar
                + share are ghost buttons. Native press scaling for
                touch feedback. All hit OS handlers rather than
                re-implementing in-app. */}
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  const q = locationQuery(event);
                  if (!q) return;
                  // iOS users get an "Apple Maps or Google Maps?" prompt
                  // the first time. Saved choice goes to localStorage so
                  // we don't keep asking. Android users go straight to
                  // Google Maps via App Links — no chooser needed.
                  const platform = detectPlatform();
                  if (platform === 'ios') {
                    const pref = getMapPreference();
                    if (pref === 'apple')  { window.open(appleMapsUrl(q),  '_blank', 'noopener,noreferrer'); return; }
                    if (pref === 'google') { window.open(googleMapsUrl(q), '_blank', 'noopener,noreferrer'); return; }
                    setMapPickerOpen(true);
                    return;
                  }
                  window.open(directionsUrl(q), '_blank', 'noopener,noreferrer');
                }}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Directions
              </button>
              {kickoff && (
                <button
                  type="button"
                  onClick={() => {
                    try {
                      const ics = buildIcs({
                        event,
                        eventId,
                        kickoff,
                        endDate,
                        endsAt,
                        displayTitle: eventDisplayTitle(event),
                      });
                      const safeName = (eventDisplayTitle(event) || 'event')
                        .replace(/[^a-z0-9]+/gi, '-')
                        .toLowerCase();
                      downloadIcs(`scrimly-${safeName}.ics`, ics);
                      toast.success('Calendar file downloaded');
                    } catch (err) {
                      console.error('[ics]', err);
                      toast.error('Could not generate calendar file');
                    }
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all active:scale-[0.97]"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <CalendarDaysIcon className="h-4 w-4 text-[#5a8a1f]" />
                  Add to calendar
                </button>
              )}
              <button
                type="button"
                onClick={async () => {
                  const shareData = {
                    title: eventDisplayTitle(event),
                    text: [
                      eventDisplayTitle(event),
                      kickoff && format(kickoff, 'EEE MMM d · h:mm a'),
                      event.fieldName,
                    ].filter(Boolean).join(' · '),
                    url: window.location.href,
                  };
                  if (navigator.share) {
                    try { await navigator.share(shareData); }
                    catch (err) { /* user cancelled */ }
                  } else {
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                      toast.success('Link copied');
                    } catch {
                      toast.error('Could not copy link');
                    }
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <ShareIcon className="h-4 w-4 text-[#5a8a1f]" />
                Share
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PARENT RSVP — let parents tap Yes/No directly on the
            event detail page instead of waiting for the SMS link.
            Shown only for parents (the block self-hides if they have
            no matching registration), only on upcoming events. */}
      {isParent && !isPastEvent && event.status !== 'cancelled' && (
        <ParentRsvpBlock event={event} eventId={eventId} />
      )}

      {/* ── UPCOMING: SMS RSVP breakdown ── */}
      {/* RSVP Status block — coach only.
          Parents shouldn't see totals or the per-player list (PII privacy:
          other parents' names + RSVP status). Their own RSVP for their kid
          flows through the dedicated public /game-rsvp/:gameId/:registrationId
          link sent via SMS, not this dashboard view. */}
      {canManage && !isPastEvent && event.status !== 'cancelled' && (
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5">
          <h2
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3"
            style={{ fontFamily: HEAD_FONT }}
          >
            RSVP Status
          </h2>

          {/* Summary row */}
          <div className="flex items-center gap-5 mb-4 flex-wrap">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
              <CheckCircleIcon className="h-4 w-4" /> {stats.smsYes} coming
            </span>
            <span className="flex items-center gap-1.5 text-sm font-semibold text-red-500">
              <XCircleIcon className="h-4 w-4" /> {stats.smsNo} can't make it
            </span>
            <span className="text-sm text-zinc-400">
              {stats.total - stats.smsYes - stats.smsNo} no response
            </span>
          </div>

          {/* Action row — RSVP nudge (templated, non-responders only) +
              smart-composer Send Reminder (AI-drafted, ALL parents). Two
              distinct affordances because they target different parents
              with different messaging. */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {nonResponderCount > 0 && (
              <button
                onClick={handleNudge}
                disabled={nudge.disabled}
                className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:border-zinc-200 disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                title={nudge.cooldownRemaining > 0
                  ? 'A reminder was sent recently — wait for the cooldown'
                  : 'Send an SMS reminder to parents who haven\'t responded'}
              >
                <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                {nudge.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => setComposerType('Reminder')}
              className="inline-flex items-center gap-1 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/15 hover:bg-[#b5f03d]/25 text-[#3d6010] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              title="Draft an AI reminder for this event with all the details auto-filled"
            >
              <SparklesIcon className="h-3.5 w-3.5" />
              Send Reminder
            </button>
          </div>

          {/* Per-player list */}
          {registrations.length > 0 && (
            <div className="space-y-1 border-t border-zinc-100 pt-3">
              {registrations.map(reg => {
                const rsvp = smsRsvps[reg.id];
                return (
                  <div key={reg.id} className="flex items-center justify-between gap-2 py-1.5">
                    <div>
                      <p className="text-sm text-[#0d1f14] font-medium">{reg.playerName}</p>
                      {reg.parentName && <p className="text-xs text-zinc-400">{reg.parentName}</p>}
                    </div>
                    {rsvp === 'yes' && (
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        ✓ Coming
                      </span>
                    )}
                    {rsvp === 'no' && (
                      <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
                        ✗ Can't make it
                      </span>
                    )}
                    {!rsvp && (
                      <span className="text-xs text-zinc-300">No response</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── PAST GAME: Result.
          Parents see the score IF the coach has posted one (read-only), but
          they don't see the inline score-entry form. If no result has been
          posted yet, parents see a soft "Score not posted yet" placeholder. */}
      {isPastEvent && isGame && event.status !== 'cancelled' && (
        <div id="result-section" className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5">
          <div className="flex items-center justify-between mb-3">
            <h2
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Result
            </h2>
            {canManage && result && !editingResult && (
              <button
                onClick={() => setEditingResult(true)}
                className="text-xs text-zinc-500 hover:text-[#5a8a1f] flex items-center gap-1 transition-colors uppercase font-bold tracking-wide"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <PencilSquareIcon className="h-3.5 w-3.5" /> Edit score
              </button>
            )}
          </div>

          {/* Entered result — display (visible to coach + parent) */}
          {result && !editingResult && (
            <div className="flex flex-wrap items-center gap-3">
              <div
                className={clsx(
                  'inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl border',
                  result.outcome === 'win'  ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  result.outcome === 'loss' ? 'bg-red-50     text-red-700     border-red-200'     :
                                             'bg-zinc-50    text-zinc-700    border-zinc-200'
                )}
                style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 22, letterSpacing: '0.5px' }}
              >
                <TrophyIcon className="h-5 w-5" />
                {result.text}
              </div>
              {/* Share Recap — opens the smart composer pre-filled with
                  the score, opponent, date. Coach types highlights, hits
                  send. Distinct from the Share-via-SMS templated blast
                  (a one-tap "score went out") that fires when the coach
                  ticks the box during result entry. */}
              {canManage && (
                <button
                  type="button"
                  onClick={() => setComposerType('Game Recap')}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/15 hover:bg-[#b5f03d]/25 text-[#3d6010] px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  title="Draft an AI recap with the score, opponent, and date pre-filled"
                >
                  <SparklesIcon className="h-4 w-4" />
                  Share Recap
                </button>
              )}
            </div>
          )}

          {/* No-result placeholder for parents */}
          {!result && !canManage && (
            <p className="text-sm text-zinc-400 italic">
              Score not posted yet.
            </p>
          )}

          {/* Enter / edit result — coach only */}
          {canManage && (!result || editingResult) && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-[11px] font-bold text-zinc-500 mb-1.5 uppercase tracking-[1.2px]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {event.homeAway === 'home' ? 'Our score' : 'Our score (away)'}
                  </label>
                  <input
                    type="number" min="0" value={teamScore}
                    onChange={e => setTeamScore(e.target.value)}
                    className="w-full rounded-xl border border-zinc-300 px-3 py-2 text-center font-bold text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                    style={{ fontFamily: HEAD_FONT, fontSize: 28 }}
                  />
                </div>
                <div>
                  <label
                    className="block text-[11px] font-bold text-zinc-500 mb-1.5 uppercase tracking-[1.2px]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {event.opponent || 'Opponent'}
                  </label>
                  <input
                    type="number" min="0" value={oppScore}
                    onChange={e => setOppScore(e.target.value)}
                    className="w-full rounded-xl border border-zinc-300 px-3 py-2 text-center font-bold text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                    style={{ fontFamily: HEAD_FONT, fontSize: 28 }}
                  />
                </div>
              </div>
              <label className="flex items-start gap-3 rounded-xl border border-[#b5f03d]/30 bg-[#b5f03d]/5 px-4 py-3 cursor-pointer">
                <input
                  type="checkbox" checked={shareResult} onChange={e => setShareResult(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]"
                />
                <span className="text-sm text-[#0d1f14]">
                  <span className="font-semibold">Share via SMS</span>
                  <span className="text-zinc-600"> — send final score to opted-in parents</span>
                </span>
              </label>
              <div className="flex gap-2">
                {editingResult && (
                  <button
                    onClick={() => setEditingResult(false)}
                    className="flex-1 py-2.5 rounded-xl border border-zinc-200 text-sm font-bold text-zinc-700 hover:bg-zinc-50 uppercase transition-colors"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={saveResult} disabled={savingResult}
                  className="flex-1 py-2.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] text-sm font-bold uppercase transition-colors disabled:opacity-50"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {savingResult ? 'Saving…' : 'Save result'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PAST: Attendance roll-call ── coach only.
          Parents see a stripped-down summary on the SeasonSchedule cards
          (present/absent counts) but never the per-player roster + edit
          controls (PII privacy + management surface). */}
      {canManage && isPastEvent && event.status !== 'cancelled' && (
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5">
          {/* Section header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}
              >
                Attendance
              </h2>
              {!sessionLoading && (
                <p className="text-sm font-semibold text-[#0d1f14] mt-0.5">
                  {stats.present} / {stats.total} marked present
                </p>
              )}
            </div>
            {dirty && (
              <button
                onClick={saveAttendance} disabled={saving}
                className="px-4 py-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] text-sm font-bold uppercase transition-colors disabled:opacity-50"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>

          {/* Session loading */}
          {sessionLoading && (
            <div className="space-y-2">
              {[1, 2, 3].map(n => <div key={n} className="h-14 rounded-xl bg-zinc-100 animate-pulse" />)}
            </div>
          )}

          {/* No confirmed players */}
          {!sessionLoading && registrations.length === 0 && (
            <div className="rounded-xl border-2 border-dashed border-zinc-200 p-8 text-center">
              <UserGroupIcon className="mx-auto h-8 w-8 text-zinc-300 mb-2" />
              <p className="text-sm text-zinc-400">No confirmed players for this season yet.</p>
            </div>
          )}

          {/* Roll-call UI */}
          {!sessionLoading && registrations.length > 0 && (
            <>
              {/* Filter chips
                  Audit fix #4: horizontal scroll instead of flex-wrap so the
                  4 chips stay on a single row even on a 360px viewport. The
                  negative margin + matching padding lets the row bleed to
                  the card edge so the rightmost chip can scroll fully into
                  view; snap-x makes the scroll feel intentional. */}
              <div className="flex items-center gap-2 mb-3 overflow-x-auto -mx-5 px-5 snap-x scrollbar-hide">
                {[
                  { key: 'all',            label: `All ${stats.total}`,            active: 'bg-[#0d1f14] text-white border-[#0d1f14]',           inactive: 'bg-white text-zinc-600 border-zinc-200' },
                  { key: STATUS.present,   label: `✓ Present ${stats.present}`,    active: 'bg-emerald-600 text-white border-emerald-600',       inactive: 'bg-white text-emerald-700 border-emerald-200' },
                  { key: STATUS.absent,    label: `✗ Absent ${stats.absent}`,      active: 'bg-red-500 text-white border-red-500',               inactive: 'bg-white text-red-500 border-red-200' },
                  ...(stats.unmarked > 0
                    ? [{ key: STATUS.unset, label: `● Unmarked ${stats.unmarked}`, active: 'bg-[#0d1f14] text-white border-[#0d1f14]',          inactive: 'bg-white text-zinc-500 border-zinc-200' }]
                    : []),
                ].map(chip => (
                  <button
                    key={chip.key} onClick={() => setAttFilter(chip.key)}
                    className={clsx(
                      'snap-start shrink-0 px-3 py-1 rounded-full text-xs font-bold uppercase border transition-colors',
                      attFilter === chip.key ? chip.active : chip.inactive
                    )}
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>

              {/* Bulk mark */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-zinc-500">Mark all:</span>
                <button
                  onClick={() => markAll(STATUS.present)}
                  className="text-xs font-bold uppercase px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  Present
                </button>
                <button
                  onClick={() => markAll(STATUS.absent)}
                  className="text-xs font-bold uppercase px-2.5 py-1 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  Absent
                </button>
                <button
                  onClick={() => markAll(STATUS.unset)}
                  className="text-xs font-bold uppercase px-2.5 py-1 rounded-lg bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  Clear
                </button>
              </div>

              {/* Audit fix #6: inline save-error banner with a retry path.
                  The toast that fires alongside vanishes after a few seconds;
                  this banner persists until the next successful save so the
                  coach has a visible recovery action. */}
              {inlineSaveError && (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-red-700">Couldn't save attendance</p>
                    <p className="text-xs text-red-600/80 mt-0.5 break-words">{inlineSaveError}</p>
                  </div>
                  <button
                    type="button"
                    onClick={saveAttendance}
                    disabled={saving}
                    className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-red-300 bg-white hover:bg-red-100 px-3 py-1.5 text-xs font-bold uppercase text-red-700 transition-colors disabled:opacity-50"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                    {saving ? 'Retrying…' : 'Retry save'}
                  </button>
                </div>
              )}

              {/* Player rows */}
              <div className="space-y-2">
                {filteredPlayers.map(player => {
                  const status  = records[player.id] ?? STATUS.unset;
                  const smsRsvp = smsRsvps[player.id];
                  return (
                    <div
                      key={player.id}
                      onClick={() => toggle(player.id)}
                      className={clsx(
                        'flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all select-none',
                        status === STATUS.present && 'border-emerald-200 bg-emerald-50',
                        status === STATUS.absent  && 'border-red-200 bg-red-50',
                        status === STATUS.unset   && 'border-zinc-200 bg-white hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5',
                      )}
                    >
                      {/* Status circle */}
                      <div className={clsx(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                        status === STATUS.present && 'border-emerald-600 bg-emerald-600',
                        status === STATUS.absent  && 'border-red-500 bg-red-500',
                        status === STATUS.unset   && 'border-zinc-300 bg-white',
                      )}>
                        {status === STATUS.present && <CheckSolid   className="h-5 w-5 text-white" />}
                        {status === STATUS.absent  && <XCircleIcon  className="h-5 w-5 text-white" />}
                      </div>

                      {/* Player info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#0d1f14] truncate">{player.playerName}</p>
                        {player.parentName && (
                          <p className="text-xs text-zinc-400 truncate">{player.parentName}</p>
                        )}
                      </div>

                      {/* SMS RSVP hint */}
                      {smsRsvp === 'yes' && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0 whitespace-nowrap">
                          Said yes
                        </span>
                      )}
                      {smsRsvp === 'no' && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 shrink-0 whitespace-nowrap">
                          Said no
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Sticky save row */}
              {dirty && (
                <div className="mt-4 pt-3 border-t border-zinc-100">
                  <button
                    onClick={saveAttendance} disabled={saving}
                    className="w-full py-3 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] text-sm font-bold uppercase transition-colors disabled:opacity-50"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                  >
                    {saving
                      ? 'Saving…'
                      : `Save · ${stats.present} present · ${stats.absent} absent · ${stats.unmarked} unmarked`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Cancelled banner ── */}
      {event.status === 'cancelled' && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0" />
          <p className="text-sm text-red-700 font-medium">This event was cancelled.</p>
        </div>
      )}
      </div>

      {/* Smart composer slide-over — opened by Share Recap (past games)
          and Send Reminder (upcoming events) buttons elsewhere on this
          page. Single instance handles both via the messageType prop;
          pre-fills from `event` automatically. */}
      <NotifyComposerSlideOver
        open={!!composerType}
        onClose={() => setComposerType(null)}
        event={event}
        teamId={event?.teamId}
        messageType={composerType}
      />
    </div>
  );
}
